import { Box } from "@chakra-ui/react"
import {
  useContext,
  useRef,
  useMemo,
  useEffect,
  memo,
  useState,
  useCallback
} from "react"
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil"
import { SDKContext } from "../../../context/SDKContext"
import { badgeIdsSelector } from "../../../recoil/atoms/badges"
import { bannerIdsSelector } from "../../../recoil/atoms/banners"
import {
  chatAttachmentDataAtom,
  chatAttachmentReplyDataAtom,
  chatIdsSelector,
  chatsSelector,
  currentChatGenerationAtom,
  isSavingChatAttachment,
  isSavingChatAttachmentReply,
  previouslyPublishedChat,
  selectedReplyAtom,
  updateChatSelector
} from "../../../recoil/atoms/chats"
import { overlaySelector } from "../../../recoil/atoms/overlay"
import { ChatReplyInput } from "./_chatReplyInput"
import { ChatReplyMessage } from "./_chatReplyMessage"
import { ConnectorMessage } from "./_connectorMessage"
import { ChatMessage } from "./_chatMessage"
import { ChatStyles } from "./index"
import { useLocalUserType } from "../../../hooks/useLocalUserType"
import {
  localUserIdAtom,
  localUserStaticPropsAtom
} from "../../../recoil/atoms/users"
import { MessageModel, ReplyModel } from "src/recoil/atoms/chats/types"
import { useWatchPartyController } from "src/hooks/useWatchParty"
import { useVideoPopup } from "src/components/mediaPanel/floating-container/useVideoPopup"
import { useCurrentAvatarInfo } from "src/conditionals/Avatar"
import { watchPartyScreenLinkSelector } from "src/recoil/atoms/watchPartyScreen"

const ChatStatus = window.MeetOnline.ChatStatus
const ChatType = window.MeetOnline.ChatType

interface ChatBubbleProps {
  messageId: string
  isFirstMessage?: boolean
  isMobile?: boolean
  isTransparent?: boolean
  setSessionStorage?: (data: any, type: string) => void
  styleschat: ChatStyles
  canSendChat: boolean
  chatApprovalRequired: boolean
  isLastMessage?: boolean
  handleTouchStart?: any
  handleTouchEnd?: any
  alignChatRight?: boolean
}

const _ChatBubble = ({
  messageId,
  isFirstMessage,
  isMobile,
  isTransparent,
  setSessionStorage,
  styleschat,
  canSendChat,
  chatApprovalRequired,
  isLastMessage,
  handleTouchEnd,
  handleTouchStart,
  alignChatRight
}: ChatBubbleProps) => {
  const chatBubbleRef = useRef<HTMLDivElement>(null)
  const localUserProps = useRecoilValue(localUserStaticPropsAtom)
  const [selectedReplyId, setSelectedReplyId] =
    useRecoilState(selectedReplyAtom)

  const { isAudience, isModerator } = useLocalUserType()

  const [chat, setChat] = useRecoilState(chatsSelector(messageId))

  const setChats = useSetRecoilState(chatIdsSelector)
  const setBanners = useSetRecoilState(bannerIdsSelector)

  const setBadges = useSetRecoilState(badgeIdsSelector)

  const { toggleVideoPopup } = useVideoPopup()
  const { startWatchParty, stopWatchParty } = useWatchPartyController()

  const meetingClient = useContext(SDKContext)
  const [attachment, setAttachment] = useRecoilState(chatAttachmentDataAtom)
  const [attachmentReply, setReplyAttachment] = useRecoilState(
    chatAttachmentReplyDataAtom
  )
  const setIsSavingAttachment = useSetRecoilState(isSavingChatAttachment)
  const setIsSavingChatAttachmentReply = useSetRecoilState(
    isSavingChatAttachmentReply
  )
  const avatarConfig = useCurrentAvatarInfo()

  const isLocalUserChatAuthor = useMemo(() => {
    return localUserProps?.id === chat?.author?.id ? true : false
    // eslint-disable-next-line hooks/exhaustive-deps
  }, [chat?.author?.id])

  const isApproved = useMemo(() => {
    return chat?.chatStatus === ChatStatus.APPROVED ||
      chat?.chatStatus === ChatStatus.AUTO_APPROVED
      ? true
      : false
  }, [chat?.chatStatus])

  const isPendingForAction = useMemo(() => {
    return chat?.chatStatus === ChatStatus.NEW ? true : false
  }, [chat?.chatStatus])

  const isPrivateMessage = useMemo(() => {
    return chat?.chatType === ChatType.PRIVATE ? true : false
  }, [chat?.chatType])

  const currentOverlay = useRecoilValue(overlaySelector)

  const watchPartyScreenLink = useRecoilValue(watchPartyScreenLinkSelector)

  const [isReply, setIsReply] = useState(false)

  useEffect(() => {
    const lastReplyHasReturnedAfterSave =
      !!chat?.replyChats?.at(-1)?.attachmentThumbnailURL

    if (lastReplyHasReturnedAfterSave) {
      setIsReply(false)
    }
  }, [chat])

  const borderColor = useMemo(() => {
    if (isAudience) {
      return chat?.published
        ? styleschat?.servicePanelElementBorder ?? "#718096"
        : styleschat?.servicePanelElementBorder ?? "#E2E8F0"
    }

    if (chat?.published) {
      return styleschat?.servicePanelElementBorder ?? "#718096"
    }
    if (chat?.published && !isReply) {
      return styleschat?.servicePanelElementBorder
        ? styleschat?.servicePanelElementBorder
        : "#E2E8F0"
    }

    if (isReply) {
      return styleschat?.servicePanelElementBorder
        ? styleschat?.servicePanelElementBorder
        : "#E2E8F0"
    }

    return styleschat?.servicePanelElementBorder
      ? styleschat?.servicePanelElementBorder
      : "#E2E8F0"
  }, [
    isAudience,
    chat?.published,
    isReply,
    styleschat?.servicePanelElementBorder
  ])

  // toggle the reply flag after unselected or other chat message is selected
  // TODO: find a best way to do following task
  useEffect(() => {
    if (!selectedReplyId || selectedReplyId?.id !== chat?.id) setIsReply(false)
  }, [selectedReplyId])

  const _localUserId = useRecoilValue(localUserIdAtom)
  const [previousChat, setPreviousChat] = useRecoilState(
    previouslyPublishedChat
  )
  const updateChatById = useSetRecoilState(updateChatSelector)
  const unpublishChat = (previousChat: Partial<MessageModel>) => {
    updateChatById({
      ...previousChat,
      published: false,
      replyChats: previousChat.replyChats.map(c => ({ ...c, published: false }))
    })
  }

  const toggleShowAttachment = useCallback(
    (chatOrReply: Partial<MessageModel> | Partial<ReplyModel>) => {
      if (previousChat?.id) {
        unpublishChat(previousChat)
      }

      if ("replyChats" in chatOrReply) {
        setPreviousChat(chatOrReply)

        // is a chat message
        if (chatOrReply.published) {
          stopWatchParty()
        } else {
          startWatchParty(chatOrReply.attachmentURL)
        }
        toggleVideoPopup(!chatOrReply.published)
        setChat({ published: !chatOrReply?.published })
      } else {
        setPreviousChat(chat)

        // is a reply chat message, work on parent chat item
        const replyChats = chat.replyChats.map(item => {
          if (item.id === chatOrReply.id) {
            if (item.published) {
              stopWatchParty()
            } else {
              startWatchParty(item.attachmentURL)
            }
            toggleVideoPopup(!item.published)
            return { ...item, published: !item.published }
          }
          return { ...item, published: false }
        })
        setChat({ replyChats })
      }
    },
    [
      previousChat,
      setPreviousChat,
      meetingClient,
      meetingClient?.rtc,
      chat,
      setChat
    ]
  )

  const toggleChat = useCallback(() => {
    if (chat?.published) {
      setChat({ published: false })
      meetingClient?.unPublishOverlay(
        `${currentOverlay?.id}`,
        currentOverlay?.type
      )
    } else {
      if (currentOverlay && currentOverlay !== undefined) {
        if (currentOverlay.type === "banner")
          setBanners({
            name: "UPDATE",
            id: currentOverlay.id,
            attributes: { published: false }
          })
        if (currentOverlay.type === "badge")
          setBadges({
            name: "UPDATE",
            id: currentOverlay.id,
            attributes: { published: false }
          })
        if (currentOverlay.type === "chat")
          if (currentOverlay.parentChatId !== undefined) {
            setChats({
              type: "UPDATE_REPLY",
              payload: {
                id: currentOverlay.id,
                parentChatId: currentOverlay.parentChatId,
                attributes: { published: false }
              }
            })
          } else {
            setChats({
              type: "UPDATE",
              payload: {
                id: currentOverlay.id,
                attributes: { published: false }
              }
            })
          }
      }

      setChat({ published: true })

      let data = {}

      if (!currentOverlay) {
        data = {
          ...chat,
          type: "chat",
          height: chatBubbleRef?.current?.offsetHeight,
          published: true
        }
      } else {
        data = {
          ...chat,
          type: "chat",
          height: chatBubbleRef?.current?.offsetHeight,
          published: true,
          unpublishedId: currentOverlay.id
        }
      }

      meetingClient?.publishOverlay(data)
    }
    // eslint-disable-next-line hooks/exhaustive-deps
  }, [chat?.published, currentOverlay])

  const toggleOvelayReply = useCallback(
    (target, ref) => {
      setChat({ published: false })
      if (target.published) {
        const replys = chat?.replyChats.map(item => {
          if (item.id === target.id) {
            const result = { ...item, published: false }
            return result
          }
          return item
        })
        setChat({ replyChats: replys })
        meetingClient?.unPublishOverlay(
          currentOverlay?.id,
          currentOverlay?.type
        )
      } else {
        if (currentOverlay && currentOverlay !== undefined) {
          // meetingClient?.unPublishOverlay(
          //   currentOverlay?.id,
          //   currentOverlay?.type
          // )
          if (currentOverlay.type === "banner")
            setBanners({
              name: "UPDATE",
              id: currentOverlay.id,
              attributes: { published: false }
            })
          if (currentOverlay.type === "badge")
            setBadges({
              name: "UPDATE",
              id: currentOverlay.id,
              attributes: { published: false }
            })
          if (currentOverlay.type === "chat")
            setChats({
              type: "UPDATE",
              payload: {
                id: currentOverlay.id,
                attributes: { published: false }
              }
            })
        }
        const replys = chat?.replyChats.map(item => {
          if (item.id === target.id) {
            const result = { ...item, published: true }
            return result
          }
          if (item.id === currentOverlay?.id) {
            const result = { ...item, published: false }
            return result
          }
          return item
        })
        setChat({ replyChats: replys })
        let data = {}

        if (!currentOverlay) {
          data = {
            ...target,
            type: "chat",
            height: ref?.current?.offsetHeight,
            published: true
          }
        } else {
          data = {
            ...target,
            type: "chat",
            height: ref?.current?.offsetHeight,
            published: true,
            unpublishedId: currentOverlay.id
          }
        }
        meetingClient?.publishOverlay(data)
      }
    },
    [
      chat,
      currentOverlay,
      meetingClient,
      setBadges,
      setBanners,
      setChat,
      setChats
    ]
  )

  const [positionModal, setPositionModal] = useState({ x: 0, y: 0 })
  const generation = useRecoilValue(currentChatGenerationAtom)

  const handleAssignPosition = useCallback(
    event => {
      if (isMobile) {
        setPositionModal({ x: 20, y: event.clientY + 120 })
      } else {
        setPositionModal({ x: 395, y: event.clientY + 180 })
      }
    },
    [isMobile]
  )

  const scrollDown = useCallback(() => {
    const messagesContainer = document.getElementById("messages-container")
    const distance = messagesContainer?.scrollTop + 60

    setTimeout(() => {
      if (messagesContainer) messagesContainer.scrollTop = distance
    }, 200)
  }, [])

  const srollToDownReplyMobile = useCallback(
    (id: string, isReply: boolean = false) => {
      const messagesContainer = document.getElementById(
        "messages-container-mobile"
      ) as HTMLDivElement

      const message = isReply
        ? (document.getElementById(`reply-${id}`) as HTMLDivElement)
        : (document.getElementById(`message-${id}`) as HTMLDivElement)

      const messagesContainerBottomBorder: number =
        messagesContainer?.getBoundingClientRect().bottom

      const messageBottomBorder: number =
        message?.getBoundingClientRect().bottom

      const bottomZone: boolean =
        messageBottomBorder > messagesContainerBottomBorder - 100

      const distance: number = messagesContainer?.scrollTop + 100

      if (bottomZone) {
        let timer: null | ReturnType<typeof setTimeout> = null
        timer = setTimeout(() => {
          messagesContainer!.scrollTop = distance
        }, 200)
        return () => clearTimeout(timer)
      }
    },
    []
  )

  const srollToDownReply = useCallback((id: string, isReply: boolean) => {
    const messagesContainer = document.getElementById(
      "messages-container"
    ) as HTMLDivElement
    const message = isReply
      ? (document.getElementById(`reply-${id}`) as HTMLDivElement)
      : (document.getElementById(`message-${id}`) as HTMLDivElement)

    const messagesContainerBottomBorder: number =
      messagesContainer?.getBoundingClientRect().bottom

    const messageBottomBorder: number = message?.getBoundingClientRect().bottom

    const bottomZone: boolean =
      messageBottomBorder > messagesContainerBottomBorder - 100

    const distance: number = messagesContainer?.scrollTop + 60

    if (bottomZone) {
      let timer: null | ReturnType<typeof setTimeout> = null
      timer = setTimeout(() => {
        messagesContainer.scrollTop = distance
      }, 200)
      return () => clearTimeout(timer)
    }
  }, [])

  const handleReply = useCallback(
    (id: string, isReplyOpen?: boolean) => {
      if (attachment.data) {
        setReplyAttachment({ ...attachment })
        setAttachment({ data: "", mimeType: "" })
      }

      setIsReply(prev => !prev)
      if (!isReply && !isMobile) {
        /* scrollDown() */
        srollToDownReply(id, isReplyOpen)
      }

      if (!isReply && isMobile) {
        srollToDownReplyMobile(id)
      }

      if (isMobile) {
        // set the selected reply id and if already selected than remove it
        setSelectedReplyId(isReply ? null : { id })
      }
    },
    [isMobile, isReply, srollToDownReply, srollToDownReplyMobile, attachment]
  )

  const handleInnerReply = useCallback(
    (id: string, isReplyOpen?: boolean) => {
      if (!isReply && !isMobile) {
        setIsReply(prev => !prev)
        /* scrollDown() */
        srollToDownReply(id, isReplyOpen)
      }

      if (!isReply && isMobile) {
        srollToDownReplyMobile(id)
        // selectedReplyMessageId(id)
      }
    },
    [isMobile, isReply, srollToDownReply, srollToDownReplyMobile]
  )

  const handleChatMessageDelete = useCallback(
    id => {
      meetingClient?.deleteChatMessage(id)
      setIsReply(false)

      const isHierarchyPublished =
        chat.published || chat.replyChats?.some(c => c.published)

      if (isHierarchyPublished) {
        meetingClient?.rtc?.stopWatchParty()
        meetingClient?.sendStoppingWatchParty(_localUserId)
        setPreviousChat(null)
      }
    },
    [meetingClient, chat]
  )

  const lastReplyId = useMemo(() => {
    if (chat?.replyChats.length !== 0) {
      for (let i = chat?.replyChats.length - 1; i >= 0; i--) {
        if (!chat?.replyChats[i].badReply) {
          return chat?.replyChats[i].id
        }
      }
    }
    return undefined
  }, [chat?.replyChats])
  const handleSendMessage = useCallback(
    (chatMessage: string, parentChatId: string, userId: string) => {
      let immediateParentChatId
      if (parentChatId) {
        immediateParentChatId = lastReplyId ? lastReplyId : parentChatId
      }

      if (attachmentReply.data) {
        meetingClient?.sendPublicChatAttachment({
          chatMessage,
          attachment: attachmentReply.data,
          userId,
          immediateParentChatId,
          parentChatId,
          toAvatarId: avatarConfig.id.toString(),
          generation
        })
        setIsSavingChatAttachmentReply(true)
      } else if (chatMessage.length) {
        meetingClient?.sendPublicChat(
          chatMessage,
          parentChatId,
          userId,
          immediateParentChatId,
          avatarConfig?.id.toString(),
          generation
        )
      }
    },
    [lastReplyId, meetingClient, attachment.data, attachmentReply.data]
  )

  const marginTopBubble = useMemo(() => {
    if (isMobile) {
      return isTransparent ? "calc(0.5rem - 3px)" : "calc(0.5rem - 1px)"
    }
    return !isFirstMessage && !isMobile ? "0.5rem" : "7px"
  }, [isFirstMessage, isMobile, isTransparent])

  if (!chat) {
    return null
  }

  // Hide the attachment, if it is shown on MVW
  if (chat?.attachmentURL) {
    if (chat?.attachmentURL === watchPartyScreenLink) {
      return null
    }
  }

  return (
    <Box
      alignSelf={alignChatRight ? "flex-end" : "unset"}
      flexDirection="column"
      key={`chat-${messageId}`}
      id={`chat-${messageId}`}
      marginTop={marginTopBubble}
      marginBottom={isLastMessage && (!isMobile ? "0.6rem" : "2px")}
      maxW={isMobile && "calc(100% - 1rem)"}
      onTouchStart={handleTouchStart ? handleTouchStart : undefined}
      onTouchEnd={handleTouchEnd ? handleTouchEnd : undefined}
    >
      <ChatMessage
        chat={chat}
        chatApprovalRequired={chatApprovalRequired}
        styleschat={styleschat}
        canSendChat={canSendChat}
        borderColor={borderColor}
        isPendingForAction={isPendingForAction}
        isApproved={isApproved}
        isModerator={isModerator}
        isPrivateMessage={isPrivateMessage}
        chatBubbleRef={chatBubbleRef}
        toggleChat={toggleChat}
        toggleShowAttachment={toggleShowAttachment}
        handleChatMessageDelete={handleChatMessageDelete}
        handleReply={handleReply}
        isMobile={isMobile}
        positionModal={positionModal}
        handleAssignPosition={handleAssignPosition}
        setIsReply={setIsReply}
        isReply={isReply}
        isAudience={isAudience}
        isTransparent={isTransparent}
        isShowReplyIcon={
          chat?.replyChats === undefined || chat?.replyChats.length === 0
        }
        colorDisk={chat?.author?.color}
        scrollDown={scrollDown}
        setSessionStorage={setSessionStorage}
        localUserName={localUserProps?.name}
        localUserId={localUserProps?.id}
      />

      {chat?.replyChats &&
        chat?.replyChats.map((reply, index, array) => (
          <Box
            marginLeft="1rem"
            zIndex={array.length + 1 - index}
            marginRight={isMobile && "1.5rem"}
            id={`replyId-${reply.id}`}
            key={`replyKey-${reply.id}`}
          >
            <ConnectorMessage
              borderColor={borderColor}
              isReplyMessage={true}
              isReply={isReply}
              isApproved={isApproved}
              isMobile={isMobile}
              isTransparent={isTransparent}
            />
            <ChatReplyMessage
              layerHeight={isMobile ? index : array.length - index}
              chatApprovalRequired={chatApprovalRequired}
              immediateParentChatId={index === 0 ? chat.id : lastReplyId}
              borderColor={borderColor}
              reply={reply}
              styleschat={styleschat}
              chat={chat}
              canSendChat={canSendChat}
              positionModal={positionModal}
              handleAssignPosition={handleAssignPosition}
              isApproved={isApproved}
              isModerator={isModerator}
              isPrivateMessage={isPrivateMessage}
              isMobile={isMobile}
              isTransparent={isTransparent}
              isShowReplyIcon={index === array.length - 1}
              handleReply={handleInnerReply}
              handleChatMessageDelete={handleChatMessageDelete}
              toggleOvelayReply={toggleOvelayReply}
              scrollDown={scrollDown}
              localUserName={localUserProps?.name}
              localUserId={localUserProps?.id}
              setSessionStorage={setSessionStorage}
              isAudience={isAudience}
              scrollToDownReplyMobile={srollToDownReplyMobile}
              toggleShowAttachment={toggleShowAttachment}
            />
          </Box>
        ))}

      {isReply && !isMobile && (
        <ChatReplyInput
          styleschat={styleschat}
          haveRiplys={chat?.replyChats && chat?.replyChats.length > 0}
          chatId={chat?.id}
          borderColor={borderColor}
          published={chat?.published}
          isApproved={isApproved}
          setIsReply={setIsReply}
          handleSendMessage={handleSendMessage}
          isPrivateMessage={isPrivateMessage}
          targetUser={chat?.targetUser}
          isLocalUserChatAuthor={isLocalUserChatAuthor}
          localUserId={localUserProps?.id}
          positionModal={positionModal}
          handleAssignPosition={handleAssignPosition}
          isReply={isReply}
          isModerator={isModerator}
        />
      )}
    </Box>
  )
}

export const ChatBubble = memo(_ChatBubble)
